import type { Column } from "@shared/types/data-table"
import type { ColumnManagementConfig } from "@shared/lib/tables/utils/column-management"
import type { Menu, MenuType } from "@shared/types/pos-menu.types"
import { getStaticFilterData } from "@shared/config/filter-static-data"
import {
  createStatusColumn,
  createTruncatedColumn,
  createDateColumn,
} from "./column-factories"

const TYPE_LABEL: Record<MenuType, string> = {
  'dine-in': 'Dine-In',
  takeout: 'Takeout',
  delivery: 'Delivery',
  'all-day': 'All-Day',
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
}

const STORE_LABELS = Object.fromEntries(
  getStaticFilterData('stores').map((s) => [s.value, s.label])
)

function getItemCount(menu: Menu): number {
  return menu.categories.reduce((sum, cat) => sum + cat.items.length, 0)
}

export const POS_MENU_COLUMNS: Record<string, Column<Menu>> = {
  name: createTruncatedColumn<Menu>(
    "name",
    "Name",
    200,
    (menu) => menu.name,
    "font-medium"
  ),
  type: createTruncatedColumn<Menu>(
    "type",
    "Type",
    110,
    (menu) => TYPE_LABEL[menu.type] ?? menu.type,
  ),
  store: createTruncatedColumn<Menu>(
    "storeId",
    "Store",
    160,
    (menu) => STORE_LABELS[menu.storeId] ?? menu.storeId,
  ),
  categories: {
    key: 'categories',
    header: 'Categories',
    width: 100,
    render: (menu) => (
      <span className="text-sm text-muted-foreground">{menu.categories.length}</span>
    ),
  },
  items: {
    key: 'items',
    header: 'Items',
    width: 80,
    render: (menu) => (
      <span className="text-sm text-muted-foreground">{getItemCount(menu)}</span>
    ),
  },
  hours: {
    key: 'hours',
    header: 'Hours',
    width: 130,
    render: (menu) => (
      <span className="text-sm text-muted-foreground">
        {menu.activeHoursStart} – {menu.activeHoursEnd}
      </span>
    ),
  },
  status: createStatusColumn<Menu>('pos-menu'),
  updatedAt: createDateColumn<Menu>('updatedAt', 'Updated', 130),
} as const

export const getPosMenuColumns = (): Column<Menu>[] => {
  return Object.values(POS_MENU_COLUMNS)
}

export const getPosMenuColumnConfig = (): ColumnManagementConfig[] => {
  return Object.entries(POS_MENU_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: true,
    required: ['name', 'status'].includes(key),
    order: index,
  }))
}
